import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

type MotionDivProps = HTMLMotionProps<'div'> & {
  className?: string;
};

const MotionDiv = motion.div as React.FC<MotionDivProps>;

type MotionArticleProps = HTMLMotionProps<'article'> & {
  className?: string;
};

const MotionArticle = motion.article as React.FC<MotionArticleProps>;

interface Project {
  id: number;
  title: string;
  description: string;
  tags: string[];
  image: string;
  github?: string;
  demo?: string;
}

const Projects: React.FC = () => {
  const { t } = useLanguage();
  const projects: Project[] = [
    {
      id: 1,
      title: 'Project One',
      description: 'A modern web application built with React and Node.js that helps users manage their tasks efficiently.',
      tags: ['React', 'Node.js', 'MongoDB', 'Express'],
      image: 'https://via.placeholder.com/600x400?text=Project+One',
      github: 'https://github.com/yourusername/project-one',
      demo: 'https://project-one-demo.com'
    },
    {
      id: 2,
      title: 'E-commerce Platform',
      description: 'A full-featured e-commerce platform with user authentication, product catalog, and payment integration.',
      tags: ['Next.js', 'TypeScript', 'Stripe', 'Tailwind CSS'],
      image: 'https://via.placeholder.com/600x400?text=E-commerce+Platform',
      github: 'https://github.com/yourusername/ecommerce-platform'
    },
    {
      id: 3,
      title: 'Weather Dashboard',
      description: 'A responsive weather application that displays current weather and forecast using a weather API.',
      tags: ['React', 'OpenWeather API', 'Chart.js', 'Material-UI'],
      image: 'https://via.placeholder.com/600x400?text=Weather+Dashboard',
      github: 'https://github.com/yourusername/weather-dashboard',
      demo: 'https://weather-demo.com'
    },
  ];

  return (
    <div className="py-12">
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto px-4"
      >
        <h1 className="text-4xl font-bold mb-8 text-center text-indigo-600 dark:text-indigo-400">
          {t('projects.title')}
        </h1>
        <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12">
          {t('projects.subtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <MotionArticle
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <img 
                src={project.image} 
                alt={project.title} 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">
                  {project.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex space-x-4">
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center"
                    >
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      {t('projects.view')}
                    </a>
                  )}
                  {project.demo && (
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center"
                    >
                      <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Live Demo
                    </a>
                  )}
                </div>
              </div>
            </MotionArticle>
          ))}
        </div>
      </MotionDiv>
    </div>
  );
};

export default Projects;
